import { create } from "zustand";
import { useEffect, useMemo } from "react";
import { devtools, persist } from "zustand/middleware";
import { createWorkspaceSlice, type WorkspaceSliceState } from "./slices/workspaceSlice";
import { createWorkflowSlice, type WorkflowSliceState } from "./slices/workflowSlice";
import { createRapidDataSlice, type RapidDataSliceState } from "./slices/rapidDataSlice";
import { createModelIntentSlice, type ModelIntentSliceState } from "./slices/modelIntentSlice";
import { createQrUploadSlice, type QrUploadSliceState } from "./slices/qrUploadSlice";
import { createMediaSlice, type MediaSliceState } from "./slices/mediaSlice";
import { createUserSlice, type UserSliceState } from "./slices/userSlice";
import { createSubscriptionSlice, type SubscriptionSliceState } from "./slices/subscriptionSlice";
import { createWaitlistSlice, type WaitlistSliceState } from "./slices/waitlistSlice";
import { createImproveSlice, type ImproveSliceState } from "./slices/improveSlice";
import {
    createBoxPromptCacheSlice,
    type BoxPromptCacheSliceState
} from "./slices/boxPromptCacheSlice";
import {
    InferenceQueueItem,
    InferenceAbortData,
    InferenceStatus,
    AnnotationsForFileAndFrameId,
    PredictionsForFileAndFrameSegment,
    AnnotationsForFile,
    PredictionsForFile
} from "./slices/modelIntentSlice";
import { noop } from "lodash";

export {
    type InferenceQueueItem,
    type InferenceAbortData,
    InferenceStatus,
    type AnnotationsForFileAndFrameId,
    type PredictionsForFileAndFrameSegment,
    type AnnotationsForFile,
    type PredictionsForFile
};

// Combined store state type
export type AppState = WorkspaceSliceState &
    WorkflowSliceState &
    RapidDataSliceState &
    ModelIntentSliceState &
    QrUploadSliceState &
    MediaSliceState &
    UserSliceState &
    SubscriptionSliceState &
    WaitlistSliceState &
    ImproveSliceState &
    BoxPromptCacheSliceState;

// Re-export RapidStateCreator for backward compatibility
export type { RapidStateCreator } from "./types";

// Create the store with devtools and persistence
export const useStore = create<AppState>()(
    devtools(
        persist(
            (set, get) => ({
                ...createWorkspaceSlice(set, get),
                ...createWorkflowSlice(set, get),
                ...createRapidDataSlice(set, get),
                ...createModelIntentSlice(set, get),
                ...createQrUploadSlice(set, get),
                ...createMediaSlice(set, get),
                ...createUserSlice(set, get),
                ...createSubscriptionSlice(set, get),
                ...createWaitlistSlice(set, get),
                ...createImproveSlice(set, get),
                ...createBoxPromptCacheSlice(set, get)
            }),
            {
                name: "rapid-workspace-store", // localStorage key
                // Only persist essential state for better performance
                partialize: (state) => ({
                    currentWorkspace: state.currentWorkspace,
                    apiKeys: state.apiKeys,
                    // Persist workflow URL for faster loading on refresh
                    workflow: state.workflow
                        ? {
                              id: state.workflow.id,
                              url: state.workflow.url,
                              name: state.workflow.name,
                              rapidDataId: state.workflow.rapidDataId,
                              config: state.workflow.config,
                              owner: state.workflow.owner
                          }
                        : null,
                    // Persist waitlist join status
                    joinedWaitlists: state.joinedWaitlists
                })
            }
        ),
        {
            name: "rapid-store", // devtools name
            enabled: process.env.NODE_ENV === "development"
        }
    )
);

// Export selectors for easy access
export const useWorkspaces = () => useStore((state) => state.workspaces);
export const useWorkspacesHasAPIFetched = () => useStore((state) => state.workspacesHasAPIFetched);
export const useWorkspaceUsages = () => useStore((state) => state.workspaceUsages);
export const useCurrentWorkspace = () => useStore((state) => state.currentWorkspace);
export const useCurrentWorkspaceUrl = () =>
    useStore((state) => state.currentWorkspace?.url || null);
export const useWorkspaceLoading = () => useStore((state) => state.workspacesLoading);
export const useWorkspacesInitializing = () => useStore((state) => state.workspaces === undefined);
export const useWorkspacesError = () => useStore((state) => state.workspacesError);

// Workflow selectors
export const useCurrentWorkflow = () => useStore((state) => state.getCurrentWorkflow());
export const useWorkflowUrl = () => useStore((state) => state.getWorkflowUrl());
export const useWorkflowToken = () => useStore((state) => state.getWorkflowToken());
export const useWorkflowLoading = () => useStore((state) => state.workflowLoading);
export const useWorkflowError = () => useStore((state) => state.workflowError);
export const useTokenLoading = () => useStore((state) => state.isTokenLoading());
export const useTokenError = () => useStore((state) => state.tokenError);
export const useIsWorkflowCreating = () => useStore((state) => state.isWorkflowCreating());

// Rapid Data selectors
export const useCurrentRapidData = () => useStore((state) => state.getCurrentRapidData());
export const useRapidDataRecords = () => useStore((state) => state.rapidDataRecords);
export const useRapidDataLoading = () => useStore((state) => state.currentRapidDataLoading);
export const useRapidDataError = () => useStore((state) => state.currentRapidDataError);
export const useRapidDataById = (id: string) => useStore((state) => state.getRapidDataById(id));
export const useRapidDataByWorkflowId = (workflowId: string) =>
    useStore((state) => state.getRapidDataByWorkflowId(workflowId));
export const useRapidDataByWorkspaceId = (workspaceId: string) =>
    useStore((state) => state.getRapidDataByWorkspaceId(workspaceId));

// API Key selectors
export const useCurrentWorkspaceApiKey = () =>
    useStore((state) => state.getCurrentWorkspaceApiKey());
export const useApiKeyForWorkspace = (workspaceId: string) =>
    useStore((state) => state.getApiKeyForWorkspace(workspaceId));

// Export individual action hooks for stable references
// Workspace action hooks
export const useSetWorkspaces = () => useStore((state) => state.setWorkspaces);
export const useSetWorkspaceUsages = () => useStore((state) => state.setWorkspaceUsages);
export const useSetCurrentWorkspace = () => useStore((state) => state.setCurrentWorkspace);
export const useSetApiKey = () => useStore((state) => state.setApiKey);
export const useSetWorkspaceLoading = () => useStore((state) => state.setWorkspacesLoading);
export const useSetWorkspaceError = () => useStore((state) => state.setWorkspacesError);
export const useSwitchWorkspace = () => useStore((state) => state.switchWorkspace);
export const useAddWorkspace = () => useStore((state) => state.addWorkspace);
export const useAddWorkspaceUsage = () => useStore((state) => state.addWorkspaceUsage);
export const useUpdateWorkspaces = () => useStore((state) => state.updateWorkspaces);
export const useClearWorkspaceState = () => useStore((state) => state.clearWorkspaceState);
export const useFetchWorkspaces = () => useStore((state) => state.fetchWorkspaces);
export const useFetchApiKeyForWorkspace = () => useStore((state) => state.fetchApiKeyForWorkspace);
export const useFetchCurrentWorkspaceApiKey = () =>
    useStore((state) => state.fetchCurrentWorkspaceApiKey);
export const usePersistWorkspace = () => useStore((state) => state.persistWorkspace);
export const useLoadPersistedWorkspace = () => useStore((state) => state.loadPersistedWorkspace);

// Workflow action hooks
export const useSetWorkflow = () => useStore((state) => state.setWorkflow);
export const useSetCreatingRapidWorkflow = () =>
    useStore((state) => state.setCreatingRapidWorkflow);
export const useSetWorkflowLoading = () => useStore((state) => state.setWorkflowLoading);
export const useSetWorkflowError = () => useStore((state) => state.setWorkflowError);
export const useSetWorkflowToken = () => useStore((state) => state.setWorkflowToken);
export const useSetTokenLoading = () => useStore((state) => state.setTokenLoading);
export const useSetTokenError = () => useStore((state) => state.setTokenError);
export const useCreateRapidWorkflow = () => useStore((state) => state.createRapidWorkflow);
export const useFetchWorkflowByUrl = () => useStore((state) => state.fetchWorkflowByUrl);
export const useFetchPublicWorkflowBySlug = () =>
    useStore((state) => state.fetchPublicWorkflowBySlug);
export const useFetchWorkflowToken = () => useStore((state) => state.fetchWorkflowToken);
export const useClearWorkflowState = () => useStore((state) => state.clearWorkflowState);

// Rapid Data action hooks
export const useSetRapidDataRecords = () => useStore((state) => state.setRapidDataRecords);
export const useSetCurrentRapidData = () => useStore((state) => state.setCurrentRapidData);
export const useSetRapidDataLoading = () => useStore((state) => state.setCurrentRapidDataLoading);
export const useSetRapidDataError = () => useStore((state) => state.setCurrentRapidDataError);
export const useAddRapidData = () => useStore((state) => state.addRapidData);
export const useUpdateRapidData = () => useStore((state) => state.updateRapidData);
export const useRemoveRapidData = () => useStore((state) => state.removeRapidData);
export const useClearRapidDataState = () => useStore((state) => state.clearRapidDataState);
export const useFetchRapidDataById = () => useStore((state) => state.fetchRapidDataById);
export const useFetchRapidDataByWorkflowId = () =>
    useStore((state) => state.fetchRapidDataByWorkflowId);
export const useCreateRapidData = () => useStore((state) => state.createRapidData);
export const useUpdateRapidDataOnServer = () => useStore((state) => state.updateRapidDataOnServer);
export const useDeleteRapidDataOnServer = () => useStore((state) => state.deleteRapidDataOnServer);
export const useStartListeningToRapidData = () =>
    useStore((state) => state.startListeningToRapidData);
export const useStopListeningToRapidData = () =>
    useStore((state) => state.stopListeningToRapidData);
export const useSyncRapidDataFromWorkflow = () =>
    useStore((state) => state.syncRapidDataFromWorkflow);
export const useFetchPublicRapidData = () => useStore((state) => state.fetchPublicRapidData);

// Workspace models selectors and actions
export const useWorkspaceModels = () => useStore((state) => state.workspaceModels);
export const useWorkspaceModelsLoading = () => useStore((state) => state.workspaceModelsLoading);
export const useWorkspaceModelsError = () => useStore((state) => state.workspaceModelsError);
export const useFetchWorkspaceModels = () => useStore((state) => state.fetchWorkspaceModels);

export const usePromptLabels = () => useStore((state) => state.promptLabels);
export const useAnyLabelHasDiffs = () =>
    useStore((state) => {
        for (const [, label] of state.promptLabels) {
            if (label.hasDiffs) return true;
        }
        return false;
    });
export const useAnnotationsForFileAndFrameId = () =>
    useStore((state) => state.annotationsForFileAndFrameId);
export const usePredictionsForFileAndFrameSegment = () =>
    useStore((state) => state.predictionsForFileAndFrameSegment);
export const useHasAnnotations = () => useStore((state) => state.getHasAnnotations());
export const useHasPredictions = () => useStore((state) => state.getHasPredictions());
export const useAnnotationsForFile = (fileId?: string) => {
    const allAnnotations = useStore((state) => state.annotationsForFileAndFrameId);
    return fileId ? allAnnotations[fileId] || {} : {};
};
export const usePredictionsForFile = (fileId?: string) => {
    const allPredictions = useStore((state) => state.predictionsForFileAndFrameSegment);
    return fileId ? allPredictions[fileId] || {} : {};
};

// Inference Queue selectors
export const useInferenceQueue = () => useStore((state) => state.inferenceQueue);
export const useGetInferenceQueueItemById = (id: string) =>
    useStore((state) => state.inferenceQueue.get(id));

export const useInferenceQueueItem = (id?: string) =>
    useStore((state) => (id ? state.inferenceQueue.get(id) : undefined));

// Model Intent action hooks
export const useSyncPredictionsAndAnnotationWithMediaItems = () =>
    useStore((state) => state.syncPredictionsAndAnnotationWithMediaItems);
export const useSetAnnotationsForFileAndFrameId = () =>
    useStore((state) => state.setAnnotationsForFileAndFrameId);

export const useClearAnnotationsForFile = () => useStore((state) => state.clearAnnotationsForFile);
export const useClearAllAnnotations = () => useStore((state) => state.clearAllAnnotations);
export const useSetPredictionsForFileAndFrameSegment = () =>
    useStore((state) => state.setPredictionsForFileAndFrameSegment);

export const useClearPredictionsForFile = () => useStore((state) => state.clearPredictionsForFile);
export const useClearPredictionsForFileAndFrameSegment = () =>
    useStore((state) => state.clearPredictionsForFileAndFrameSegment);
export const useClearAllPredictions = () => useStore((state) => state.clearAllPredictions);
export const useSetPromptLabels = () => useStore((state) => state.setPromptLabels);
export const useUpdatePromptLabels = () => useStore((state) => state.updatePromptLabels);
export const useSyncPromptLabels = () => useStore((state) => state.syncPromptLabels);
export const useSetPromptLabelsGlobalConfidenceThreshold = () =>
    useStore((state) => state.setPromptLabelsGlobalConfidenceThreshold);
export const useMarkLabelAsEdited = () => useStore((state) => state.markLabelAsEdited);

// Inference Queue action hooks
export const useAddToInferenceQueue = () => useStore((state) => state.addToInferenceQueue);
export const useUpdateInferenceQueueItemStatus = () =>
    useStore((state) => state.updateInferenceQueueItemStatus);
export const useUpdateInferenceQueueItemProgress = () =>
    useStore((state) => state.updateInferenceQueueItemProgress);
export const useClearInferenceQueue = () => useStore((state) => state.clearInferenceQueue);
export const useRemoveFromInferenceQueue = () =>
    useStore((state) => state.removeFromInferenceQueue);

// QR Upload selectors
export const useCurrentQrSession = () => useStore((state) => state.getCurrentSession());
export const useQrSessionFiles = () => useStore((state) => state.getSessionFiles());
export const useHasActiveQrSession = () => useStore((state) => state.hasActiveSession());
export const useQrUploadLoading = () => useStore((state) => state.currentSessionLoading);
export const useQrUploadError = () => useStore((state) => state.currentSessionError);
export const useProcessedQrFileIds = () => useStore((state) => state.getProcessedFileIds());
export const useIsProcessingQrFiles = () => useStore((state) => state.isProcessingFiles());

// QR Upload action hooks
export const useCreateQrSession = () => useStore((state) => state.createSession);
export const useEnsureValidQrSession = () => useStore((state) => state.ensureValidSession);
export const useInitializeQrSession = () => useStore((state) => state.initializeSession);
export const useClearQrSession = () => useStore((state) => state.clearSession);
export const useSetQrUploadLoading = () => useStore((state) => state.setCurrentSessionLoading);
export const useSetQrUploadError = () => useStore((state) => state.setCurrentSessionError);
export const useProcessQrFiles = () => useStore((state) => state.processQrFiles);
export const useMarkQrFileAsProcessed = () => useStore((state) => state.markFileAsProcessed);
export const useStartListeningToQrSession = () =>
    useStore((state) => state.startListeningToSession);
export const useStopListeningToQrSession = () => useStore((state) => state.stopListeningToSession);
export const useLoadQrSessionFromStorage = () => useStore((state) => state.loadSessionFromStorage);

// Simple QR Processing Effect Hook
export const useQrProcessingEffect = (params: {
    enabled: boolean;
    onFileProcessed: (file: File) => void;
    onAnalytics?: (event: string, data: any) => void;
}) => {
    const qrSessionFiles = useQrSessionFiles();
    const processQrFiles = useProcessQrFiles();
    const isProcessingFiles = useIsProcessingQrFiles();

    useEffect(() => {
        if (
            !params.enabled ||
            !qrSessionFiles ||
            qrSessionFiles.length === 0 ||
            isProcessingFiles
        ) {
            return;
        }

        console.log("🔄 QR files detected, processing:", qrSessionFiles.length, "files");
        processQrFiles(params.onFileProcessed, params.onAnalytics || noop);
    }, [
        params.enabled,
        qrSessionFiles,
        processQrFiles,
        params.onFileProcessed,
        params.onAnalytics,
        isProcessingFiles
    ]);
};

// Export getters for easy access
export const useGetCurrentWorkspace = () => useStore((state) => state.getCurrentWorkspace);
export const useCurrentWorkspaceUsage = () =>
    useStore(
        (state) =>
            state.workspaceUsages?.find((usage) => usage.id === state.currentWorkspace?.id) || null
    );
export const useGetWorkspaceById = () => useStore((state) => state.getWorkspaceById);
export const useGetWorkspaceUsageById = () => useStore((state) => state.getWorkspaceUsageById);

// Note: useWorkspaceActions was removed to prevent infinite loops.
// Use individual hooks like useSwitchWorkspace(), useFetchWorkspaces(), etc. instead.

// ================ Media selectors START ================
export const useMediaStore = () => useStore((state) => state.media);
export const useMediaUploadPromises = () => useStore((state) => state.mediaUploadPromises);
export const useCurrentMedia = () => useStore((state) => state.currentMedia);

// currentMedia.file selectors
export const useCurrentMediaFileMetadata = () =>
    useStore((state) => state.currentMedia?.file.metadata);
export const useCurrentMediaFileVideoMetadata = () =>
    useStore((state) => state.currentMedia?.file.metadata?.videoMetadata);
export const useCurrentMediaFileKeyframeOffset = () =>
    useStore((state) => state.currentMedia?.file.metadata?.keyframe_offset);
export const useCurrentMediaFileNaturalDimensions = () =>
    useStore((state) => state.currentMedia?.file.metadata?.naturalDimensions);
export const useCurrentMediaFileRenderedDimensions = () =>
    useStore((state) => state.currentMedia?.file.metadata?.renderedDimensions);
export const useCurrentMediaFileInferredDimensions = () =>
    useStore((state) => state.currentMedia?.file.metadata?.inferredDimensions);

// currentMedia.frame selectors
export const useCurrentMediaFrameInferredDimensions = () =>
    useStore((state) => state.currentMedia?.frame?.metadata?.inferredDimensions);

// media item state management
// these should only be pulled in by the useMediaUtils hook
export const useAddMediaItem = () => useStore((state) => state.addMediaItem);
export const useUpdateMediaItem = () => useStore((state) => state.updateMediaItem);
export const useDeleteMediaItem = () => useStore((state) => state.deleteMediaItem);
export const useSetCurrentMedia = () => useStore((state) => state.setCurrentMedia);

// media upload promise management
export const useDeleteMediaUploadPromise = () =>
    useStore((state) => state.deleteMediaUploadPromise);
// ================ Media selectors END ================

// ================ User selectors START ================
export const useUser = () => useStore((state) => state.user);
export const useSetUser = () => useStore((state) => state.setUser);
// ================ User selectors END ================

// ================ Subscription selectors START ================
export const usePaidPlanSubscriptionById = () =>
    useStore((state) => state.paidPlanSubscriptionById);
export const useFetchPaidPlanSubscription = () =>
    useStore((state) => state.fetchPaidPlanSubscription);
// ================ Subscription selectors END ================

// ================ Waitlist selectors START ================
export const useJoinedWaitlists = () => useStore((state) => state.joinedWaitlists);
export const useHasJoinedWaitlist = () => useStore((state) => state.hasJoinedWaitlist);
export const useJoinWaitlist = () => useStore((state) => state.joinWaitlist);
export const useLeaveWaitlist = () => useStore((state) => state.leaveWaitlist);
// ================ Waitlist selectors END ================

// ================ Improve selectors START ================
// State selectors
export const useImproveBatch = () => useStore((state) => state.getImproveBatch());
export const useImproveDatasetId = () => useStore((state) => state.getImproveDatasetId());
export const useImproveAnnotationGroup = () =>
    useStore((state) => state.getImproveAnnotationGroup());
export const useImproveSources = () => useStore((state) => state.getImproveSources());
export const useLimitedSources = () => useStore((state) => state.getLimitedSources());
export const useImproveMediaItems = () => useStore((state) => state.getImproveMediaItems());
export const useImproveLoading = () => useStore((state) => state.getImproveLoading());
export const useImproveError = () => useStore((state) => state.improveError);
export const useIsLoadingImages = () => useStore((state) => state.isLoadingImages);
export const useHasImproveInitialized = () => useStore((state) => state.hasInitialized);

// Improve action hooks
export const useSetImproveBatch = () => useStore((state) => state.setImproveBatch);
export const useSetImproveLoading = () => useStore((state) => state.setImproveLoading);
export const useSetImproveError = () => useStore((state) => state.setImproveError);
export const useSetLimitedSources = () => useStore((state) => state.setLimitedSources);
export const useSetImproveMediaItems = () => useStore((state) => state.setImproveMediaItems);
export const useSetIsLoadingImages = () => useStore((state) => state.setIsLoadingImages);
export const useSetHasImproveInitialized = () => useStore((state) => state.setHasInitialized);
export const useClearImproveState = () => useStore((state) => state.clearImproveState);
export const useClearMediaState = () => useStore((state) => state.clearMediaState);

// Pending AI tool for annotation sync (used by GeneratePredictions and useAnnotationSync)
export const usePendingAiTool = () => useStore((state) => state.getPendingAiTool());
export const useSetPendingAiTool = () => useStore((state) => state.setPendingAiTool);
export const useClearPendingAiTool = () => useStore((state) => state.clearPendingAiTool);

// Optimistic removal action for Approve Image
export const useRemoveSourceById = () => useStore((state) => state.removeSourceById);

// Load more sources when running low
export const useLoadMoreSources = () => useStore((state) => state.loadMoreSources);

// Approved count tracking
export const useApprovedCount = () => useStore((state) => state.getApprovedCount());
export const useTotalBatchCount = () => useStore((state) => state.getTotalBatchCount());
export const useIncrementApprovedCount = () => useStore((state) => state.incrementApprovedCount);
export const useApprovedImageIds = () => useStore((state) => state.getApprovedImageIds());
export const useIsImageApproved = (imageId: string) =>
    useStore((state) => state.isImageApproved(imageId));
export const useMarkImageAsApproved = () => useStore((state) => state.markImageAsApproved);
export const useApprovedAnnotationSnapshot = (imageId: string | undefined) =>
    useStore((state) => (imageId ? state.getApprovedAnnotationSnapshot(imageId) : undefined));

// Round-based progress (for stepped progress display)
export const useCurrentRound = () => useStore((state) => state.getCurrentRound());
export const useTotalRounds = () => useStore((state) => state.getTotalRounds());
export const useApprovedInCurrentRound = () =>
    useStore((state) => state.getApprovedInCurrentRound());
export const useTotalInCurrentRound = () => useStore((state) => state.getTotalInCurrentRound());
// ================ Improve selectors END ================

// ================ Box Prompt Cache selectors START ================
export const useBoxPromptCache = () => useStore((state) => state.boxPromptCache);
export const useSetBoxPromptCache = () => useStore((state) => state.setBoxPromptCache);
export const useGetBoxPromptCache = () => useStore((state) => state.getBoxPromptCache);
export const useIncrementBoxPromptCacheIndex = () =>
    useStore((state) => state.incrementBoxPromptCacheIndex);
export const useClearBoxPromptCacheForLabel = () =>
    useStore((state) => state.clearBoxPromptCacheForLabel);
export const useClearBoxPromptCacheForFrame = () =>
    useStore((state) => state.clearBoxPromptCacheForFrame);
export const useClearBoxPromptCacheForFile = () =>
    useStore((state) => state.clearBoxPromptCacheForFile);
export const useClearAllBoxPromptCache = () => useStore((state) => state.clearAllBoxPromptCache);
// ================ Box Prompt Cache selectors END ================

export default useStore;
